import React, { useEffect, useState } from 'react';
import {
  Gift, Plus, Edit3, Trash2, X, Loader2, Eye, EyeOff, ImagePlus,
  Sparkles, Coins, Dices, Wallet, Send, SlidersHorizontal, ShieldCheck,
  Headset, BadgeCheck, UserPlus, Megaphone, Power, ExternalLink, Zap, LayoutTemplate,
  Lock, CalendarDays, Ban, ListOrdered, Network, History, Ticket, Sunrise, Clock,
  TrendingDown, ArrowDownToLine,
} from 'lucide-react';
import client, { resolveApiAssetUrl } from '../lib/api';
import { UPLOAD_MAX_LABEL, uploadTooLarge } from '../lib/mediaAsset';
import { useToast } from '../hooks/use-toast';
import {
  BONUS_ELIGIBLE_TYPES, formatBonusEligible, parseBonusEligible,
  serializeBonusEligible,
} from '../constants/bonusEligibility';
import { autoBonusHighlight, bonusHighlight } from '../lib/bonusHighlight';
import RichHtmlFrame from '../components/RichHtmlFrame';
import BonusCardHtml from '../components/BonusCardHtml';
import BonusHtmlThumb from '../components/BonusHtmlThumb';
import { looksLikeHtml, plainExcerpt } from '../lib/richHtmlFrame';
import {
  adminBonusHeadline, bonusPrimaryMetric, freespinRewardText,
  isCashTierBonus, isFreespinOnlyBonus, requiresTrialCashAmount,
} from '../lib/bonusReward';
import {
  REWARD_CASH, WEEKDAYS, WITHDRAW_BLOCK_DEFAULT_HOURS, bonusMaxBonus, bonusMinDeposit,
  bonusTiers, bonusWindowText, eligibilityRuleLines, lostDepositPercent, parseWeekdays,
  ruleFlag, serializeTier, serializeWeekdays, telegramChannelProblem, tierKind, tierProblem,
  tierRangeText, withdrawBlockMode,
} from '../lib/bonusRules';
import { BonusAutoAwardFields, BonusAutoAwardLog } from './components/BonusAutoAward';
import { BonusTierTable } from './components/BonusTierTable';

const empty = {
  title: '', description: '', badge: '', image: '', bonus_percent: 100,
  min_deposit: 500, max_bonus: 5000, wager_multiplier: 30, valid_days: 7,
  eligible: 'all', new_members_only: false, is_trial_bonus: false,
  trial_amount: 0, require_telegram: false, daily_claim_limit: 0,
  // Oyuncu bonusu almadan önce bu Telegram kanalına katılmalıdır; boşsa
  // Ayarlar → Telegram Bildirim'deki topluluk kullanılır.
  telegram_channel_url: '',
  claim_via_support: false,
  // Doluysa kart düğmesi bonusu talep etmez, oyuncuyu bu site içi sayfaya
  // götürür (şans çarkı /cark, çapa /capa).
  claim_redirect_url: '',
  // Yatırım bonusu, yatırım oynanmadan talep edilmelidir (varsayılan açık).
  require_unspent_deposit: true,
  // Aynanın öteki yüzü — iade/kayıp kampanyası: bonus yatırım kaybedildikten
  // sonra alınır. İkisi birlikte açılamaz; tavan yatırımın yüzdesidir.
  require_lost_deposit: false, lost_deposit_max_percent: 10,
  highlight_note: '', highlight_note_enabled: true, card_html_enabled: true,
  active: true, order: 0,
  // Ödülün iki ayağı bağımsızdır: kampanya para, freespin ya da ikisini
  // birden verebilir. Boş bırakılan freespin alanları Freespin Kontrol
  // sayfasındaki varsayılana düşer (-1 = "ayarlanmamış", 0 geçerli değerdir).
  cash_reward_enabled: true, freespin_enabled: false, freespin_count: 0,
  freespin_game_id: '', freespin_bet: 0, freespin_valid_days: 0,
  freespin_wager_multiplier: -1,
  // Otomatik tanımlama: yatırım onaylanınca ödül (freespin ya da bakiye) talep
  // beklemeden verilir (saat penceresi Türkiye saati; kademe boşsa freespin'de
  // tek spin adedi, bakiyede yüzde/Max. Bonus uygulanır).
  auto_award: false, auto_daily_limit: 0,
  deposit_window_start: '', deposit_window_end: '', freespin_tiers: [],
  tier_exact: false,
  // Uygunluk kuralları (backend/services/bonus_eligibility.py). Yatırım kilidi
  // ve kayıp bonusu dışlaması varsayılan açıktır: bir yatırıma bir bonus.
  exclusive_deposit: true, blocks_loss_bonus: true, withdraw_block_hours: 0,
  // Çekim kuralının ölçüsü: 'net' (çekim yatırımı aşarsa / net giriş tutarın
  // altındaysa) ya da 'any' (tek çekim bile engeller).
  withdraw_block_mode: 'net', withdraw_block_min_net: 0,
  first_deposits_count: 0, stop_after_withdraw: false, daily_first_deposit: false,
  active_weekdays: '', require_no_finance_history: false, block_if_promo_used: false,
  one_per_ip: false,
};

const RULE_FLAGS = [
  'exclusive_deposit', 'blocks_loss_bonus', 'stop_after_withdraw', 'daily_first_deposit',
  'tier_exact', 'require_no_finance_history', 'block_if_promo_used', 'one_per_ip',
  'require_lost_deposit',
];

// Görseli olmayan kampanyada HTML şablon görsel alanına çizilir — sitedeki
// kartla aynı kural (components/BonusHtmlThumb.bonusHasHtmlArt). Panel ham
// açıklamayı tuttuğu için ölçü `description_html` değil açıklamanın kendisidir.
const adminHasHtmlArt = (bonus) => !!(
  bonus && !bonus.image && bonus.card_html_enabled && looksLikeHtml(bonus.description)
);

const Bonuses = () => {
  const [items, setItems] = useState([]);
  // Freespin verilebilen oyunlar ve Freespin Kontrol varsayılanları: kampanya
  // kendi değerini girmediğinde neyin geçerli olacağını panelde göstermek için.
  const [freespinGames, setFreespinGames] = useState([]);
  const [freespinDefaults, setFreespinDefaults] = useState(null);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const { toast } = useToast();
  // Kaç ödül ayağı açık: kademe tablosu yalnızca tek ödüllü kampanyada
  // anlamlıdır (tablodaki sayı spin adedi mi ₺ mi belli olmalı).
  const rewardsPicked = (form.cash_reward_enabled !== false ? 1 : 0)
    + (form.freespin_enabled ? 1 : 0);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await client.get('/admin/bonuses');
      setItems(data.items || []);
      // Freespin bilgileri yetkisi olmayan rollerde sayfayı kırmamalı.
      const [gamesRes, overviewRes] = await Promise.all([
        client.get('/admin/freespin/games', { params: { limit: 200 } })
          .catch(() => ({ data: {} })),
        client.get('/admin/freespin/overview').catch(() => ({ data: null })),
      ]);
      setFreespinGames(gamesRes.data.items || []);
      setFreespinDefaults(overviewRes.data);
    } catch (error) {
      toast({ title: 'Hata', description: error.response?.data?.detail || 'Bonuslar alınamadı' });
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const openNew = () => { setForm({ ...empty }); setModal({ mode: 'new' }); };
  const openEdit = (bonus) => {
    setForm({
      ...empty,
      ...bonus,
      // Kolon eklenmeden önce kaydedilmiş kampanyalarda alanlar NULL gelebilir;
      // form denetimleri boş/işaretli varsayılana düşer.
      highlight_note: bonus.highlight_note || '',
      highlight_note_enabled: bonus.highlight_note_enabled !== false,
      card_html_enabled: bonus.card_html_enabled !== false,
      claim_via_support: !!bonus.claim_via_support,
      claim_redirect_url: bonus.claim_redirect_url || '',
      require_telegram: !!bonus.require_telegram,
      telegram_channel_url: bonus.telegram_channel_url || '',
      require_unspent_deposit: bonus.require_unspent_deposit !== false,
      auto_award: !!bonus.auto_award,
      auto_daily_limit: Number(bonus.auto_daily_limit) || 0,
      deposit_window_start: bonus.deposit_window_start || '',
      deposit_window_end: bonus.deposit_window_end || '',
      freespin_tiers: Array.isArray(bonus.freespin_tiers) ? bonus.freespin_tiers : [],
      ...Object.fromEntries(RULE_FLAGS.map((key) => [key, ruleFlag(bonus, key)])),
      withdraw_block_hours: Number(bonus.withdraw_block_hours) || 0,
      withdraw_block_mode: withdrawBlockMode(bonus),
      withdraw_block_min_net: Number(bonus.withdraw_block_min_net) || 0,
      first_deposits_count: Number(bonus.first_deposits_count) || 0,
      active_weekdays: bonus.active_weekdays || '',
      lost_deposit_max_percent: bonus.lost_deposit_max_percent == null
        ? 10 : Number(bonus.lost_deposit_max_percent),
    });
    setModal({ mode: 'edit', id: bonus.id });
  };
  const uploadImage = async (file) => {
    if (!file) return;
    if (uploadTooLarge(file)) {
      toast({ title: 'Görsel yüklenemedi', description: `Dosya en fazla ${UPLOAD_MAX_LABEL} olabilir.` });
      return;
    }
    const formData = new FormData();
    formData.append('file', file);
    setUploadingImage(true);
    try {
      const { data } = await client.post('/admin/uploads/images', formData);
      setForm((current) => ({ ...current, image: data.url }));
      toast({ title: 'Görsel yüklendi' });
    } catch (error) {
      toast({ title: 'Yükleme başarısız', description: error.response?.data?.detail || 'Görsel yüklenemedi' });
    } finally { setUploadingImage(false); }
  };
  // Seçili oyun türleri: hiçbiri işaretli değilse kampanya tüm oyunlarda geçerli.
  const selectedTypes = parseBonusEligible(form.eligible);
  const toggleType = (id) => {
    const next = selectedTypes.includes(id)
      ? selectedTypes.filter((item) => item !== id)
      : [...selectedTypes, id];
    setForm({ ...form, eligible: serializeBonusEligible(next) });
  };

  const save = async () => {
    if (!form.cash_reward_enabled && !form.freespin_enabled) {
      toast({
        title: 'Ödül yok',
        description: 'Kampanya en az bir ödül vermelidir: para ya da freespin.',
      });
      return;
    }
    // Kademe tablosu artık elle talep edilen kampanyada da işler, bu yüzden
    // denetimi otomatik kampanyayla sınırlı değildir.
    if ((form.freespin_tiers || []).length) {
      if (form.cash_reward_enabled !== false && form.freespin_enabled) {
        toast({
          title: 'Kademe tablosu tek ödül ister',
          description: 'Tablodaki sayının spin adedi mi bonus tutarı mı olduğu belirsiz'
            + ' kalırdı. Para ödülü ile freespin’den birini kapatın.',
        });
        return;
      }
      const problem = tierProblem(form.freespin_tiers, tierKind(form));
      if (problem) {
        toast({ title: 'Kademe tablosu hatalı', description: problem });
        return;
      }
    }
    // Saat aralığı otomatik kampanyanın alanı olarak doğdu ama elle talep edilen
    // kampanyada da geçerlidir (Bonus Kısıtları → Geçerli saatler).
    if (!!form.deposit_window_start !== !!form.deposit_window_end) {
      toast({
        title: 'Saat aralığı eksik',
        description: 'Başlangıç ve bitiş saati birlikte girilmeli ya da ikisi de boş bırakılmalıdır.',
      });
      return;
    }
    if (form.freespin_enabled) {
      // Kademeli kampanyada kampanyanın kendi adedi 0 olabilir; ayar denetimi
      // en yüksek kademeyle yapılır (sunucuda bonus_auto.plan_for_validation).
      const tierMax = Math.max(0, ...bonusTiers(form).map((row) => row.count));
      const count = tierMax
        || Number(form.freespin_count) || Number(freespinDefaults?.defaults?.count) || 0;
      const bet = Number(form.freespin_bet) || Number(freespinDefaults?.defaults?.bet) || 0;
      const game = form.freespin_game_id || freespinDefaults?.defaults?.game_id || '';
      if (!count || !bet || !game) {
        toast({
          title: 'Freespin ayarı eksik',
          description: 'Oyun, spin adedi ve bahis tutarı gerekli. Boş bırakılan'
            + ' alanlar için Freespin Kontrol sayfasında varsayılan tanımlayın.',
        });
        return;
      }
    }
    if (requiresTrialCashAmount(form) && Number(form.trial_amount) <= 0) {
      toast({ title: 'Tutar gerekli', description: 'Deneme bonusu tutarı sıfırdan büyük olmalıdır.' });
      return;
    }
    // Backend adresi zaten site içiyle sınırlar; buradaki kontrol yöneticiye
    // 422 yerine anlaşılır bir mesaj gösterir.
    if (form.claim_redirect_url && !/^\/[^/]/.test(form.claim_redirect_url.trim())) {
      toast({
        title: 'Geçersiz adres',
        description: 'Yönlendirme adresi site içi bir yol olmalıdır (örn. /cark).',
      });
      return;
    }
    if ((form.description || '').length > DESCRIPTION_MAX_LENGTH) {
      toast({
        title: 'Açıklama çok uzun',
        description: `Açıklama en fazla ${DESCRIPTION_MAX_LENGTH.toLocaleString('tr-TR')} karakter olabilir.`,
      });
      return;
    }
    if (form.require_telegram && telegramChannelProblem(form.telegram_channel_url)) {
      toast({ title: 'Telegram kanalı', description: telegramChannelProblem(form.telegram_channel_url) });
      return;
    }
    if (form.new_members_only && Number(form.daily_claim_limit) > 0) {
      toast({
        title: 'Çelişkili ayar',
        description: 'Yalnızca yeni üyelere özel bonus zaten tek seferliktir; günlük limit verilemez.',
      });
      return;
    }
    setBusy(true);
    // Kademe satırları formda metin olarak durur; sunucuya sayı ve yalnızca
    // seçilen ödülün alanı gider (spin adedi ya da ₺).
    const body = {
      ...form,
      freespin_tiers: (form.freespin_tiers || []).map(
        (row) => serializeTier(row, tierKind(form)),
      ),
    };
    try {
      if (modal.mode === 'new') await client.post('/admin/bonuses', body);
      else await client.patch(`/admin/bonuses/${modal.id}`, body);
      toast({ title: 'Bonus kampanyası kaydedildi' });
      setModal(null);
      await load();
    } catch (error) {
      toast({ title: 'Hata', description: error.response?.data?.detail || 'Kaydedilemedi' });
    } finally { setBusy(false); }
  };
  const remove = async (id) => {
    if (!window.confirm('Bonus kampanyası silinsin mi?')) return;
    await client.delete(`/admin/bonuses/${id}`);
    await load();
  };
  const toggle = async (bonus) => {
    await client.patch(`/admin/bonuses/${bonus.id}`, { active: !bonus.active });
    await load();
  };

  // Pencerenin alt bandındaki özet: kampanyanın iki ödül ayağını birlikte
  // yazar, böylece yönetici uzun formu kapatmadan ne verdiğini görür.
  const summaryReward = (() => {
    const fallbackCount = freespinDefaults?.defaults?.count;
    const parts = [];
    if (form.cash_reward_enabled !== false) {
      parts.push(adminBonusHeadline(form, fallbackCount));
    }
    if (form.freespin_enabled) parts.push(freespinRewardText(form, fallbackCount));
    return parts.join(' + ') || 'ödül yok';
  })();

  return (
    <div>
      <div className="flex items-center gap-3 mb-5">
        <div className="w-11 h-11 rounded-xl flex items-center justify-center border border-cyan-400/20 bg-cyan-400/10 text-cyan-400"><Gift size={19} /></div>
        <div>
          <h1 className="text-xl font-bold">Bonus Kampanyaları</h1>
          <p className="text-xs text-slate-400">Genel kampanyaları ve varsayılan çevrim şartlarını yönetin</p>
        </div>
        <button onClick={openNew} className="admin-btn-primary ml-auto flex items-center gap-2"><Plus size={14} /> Yeni Bonus</button>
      </div>

      {loading ? (
        <div className="admin-card p-10 text-center"><Loader2 className="inline animate-spin text-cyan-400" /></div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {items.map((bonus) => (
            <article key={bonus.id} className="admin-card overflow-hidden group">
              <div className="relative aspect-[16/9] bg-slate-900">
                {bonus.image
                  ? <img src={resolveApiAssetUrl(bonus.image)} alt="" className="absolute inset-0 w-full h-full object-cover transition-transform duration-300 group-hover:scale-[1.025]" />
                  : adminHasHtmlArt(bonus) && <BonusHtmlThumb bonusId={bonus.id} html={bonus.description} title={bonus.title} />}
                {!adminHasHtmlArt(bonus) && <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/20 to-transparent" />}
                {bonus.badge && <span className="absolute top-3 left-3 badge-pill badge-yellow">{bonus.badge}</span>}
                <span className={`absolute top-3 right-3 badge-pill ${bonus.active ? 'badge-green' : 'badge-gray'}`}>{bonus.active ? 'Aktif' : 'Pasif'}</span>
                {/* Oranı yalnızca panel gösterir: oyuncunun kartında yüzde
                    yazılmaz (bkz. lib/bonusReward.js). */}
                {/* Şablon görsel alanındaysa başlık onun üstüne binerdi; ödül
                    zaten alttaki koşul şeridinde yazar. */}
                {!adminHasHtmlArt(bonus) && (
                  <div className="absolute bottom-3 left-3 font-black text-yellow-400 text-2xl">
                    {adminBonusHeadline(
                      bonus, freespinDefaults?.defaults?.count,
                    )}
                  </div>
                )}
              </div>
              <div className="p-4 space-y-3">
                <div>
                  <h2 className="font-bold text-sm">{bonus.title}</h2>
                  {/* Sitedeki kartla aynı: "Kartta da tasarımıyla göster"
                      açıksa şablon kırpılmış çerçevede, değilse düz özet. */}
                  {/* Şablon görsel alanındaysa altında ikinci kez çizilmez. */}
                  {adminHasHtmlArt(bonus) ? null : bonus.card_html_enabled && looksLikeHtml(bonus.description) ? (
                    <BonusCardHtml
                      html={bonus.description}
                      title={bonus.title}
                      onOpen={() => openEdit(bonus)}
                      fadeColor="rgb(16, 16, 22)"
                      className="mt-2"
                    />
                  ) : (
                    <p className="mt-1 text-xs text-slate-400 line-clamp-2">{plainExcerpt(bonus.description)}</p>
                  )}
                </div>
                {/* Sitedeki kartla aynı: deneme bonusunda "Yatırım" sütunu çizilmez. */}
                <div className={`grid ${bonus.is_trial_bonus ? 'grid-cols-2' : 'grid-cols-3'} gap-2 border-y border-white/5 py-3 text-xs`}>
                  <div>
                    <div className="text-slate-500">
                      {bonusPrimaryMetric(
                        bonus, freespinDefaults?.defaults?.count,
                      ).label}
                    </div>
                    <div className="font-bold text-cyan-400">
                      {bonusPrimaryMetric(
                        bonus, freespinDefaults?.defaults?.count,
                      ).value}
                    </div>
                  </div>
                  {!bonus.is_trial_bonus && (
                    <div>
                      <div className="text-slate-500">
                        {isFreespinOnlyBonus(bonus) || isCashTierBonus(bonus) ? 'Min. Yatırım' : 'Max. Bonus'}
                      </div>
                      <div className="font-bold text-green-400">
                        {isFreespinOnlyBonus(bonus) || isCashTierBonus(bonus)
                          ? bonusMinDeposit(bonus) : bonusMaxBonus(bonus)} ₺
                      </div>
                    </div>
                  )}
                  <div><div className="text-slate-500">Çevrim</div><div className="font-bold text-yellow-400">x{bonus.wager_multiplier}</div></div>
                </div>
                <div className="text-[11px] text-slate-400">Geçerli alan: <span className="font-semibold text-slate-300">{formatBonusEligible(bonus.eligible)}</span></div>
                {bonus.is_trial_bonus && <div className="text-[11px] font-semibold text-cyan-400">Deneme bonusu • Yatırım gerektirmez</div>}
                {bonus.require_telegram && (
                  <div className="text-[11px] font-semibold text-sky-300">
                    Telegram kanalına katılım zorunlu
                    {bonus.telegram_channel_url ? ` • ${bonus.telegram_channel_url.replace(/^https?:\/\//, '')}` : ''}
                  </div>
                )}
                {bonus.new_members_only && <div className="text-[11px] font-semibold text-emerald-400">{bonus.is_trial_bonus ? 'Yalnızca yeni üyelere özel' : 'Yalnızca yeni üyelerin ilk yatırımı'}</div>}
                {Number(bonus.daily_claim_limit) > 0 && !bonus.claim_via_support && <div className="text-[11px] font-semibold text-amber-400">24 saatte {Number(bonus.daily_claim_limit)} kez alınabilir</div>}
                {bonus.claim_via_support && <div className="text-[11px] font-semibold text-emerald-400">Canlı destekten talep edilir (manuel)</div>}
                {!!bonus.claim_redirect_url && <div className="text-[11px] font-semibold text-cyan-400">{bonus.claim_redirect_url} sayfasına yönlendirir</div>}
                {/* Kural kapatıldığında bunu kartta söylemek gerekir: bonus,
                    harcanmış yatırıma da verilir. */}
                {!bonus.is_trial_bonus && bonus.require_unspent_deposit === false
                  && !bonus.require_lost_deposit && <div className="text-[11px] font-semibold text-amber-400">Yatırım harcandıktan sonra da alınabilir</div>}
                {!bonus.is_trial_bonus && !!bonus.require_lost_deposit && (
                  <div className="text-[11px] font-semibold text-amber-400">
                    İade bonusu: yatırım kaybedildikten sonra alınır
                  </div>
                )}
                {bonus.auto_award && (
                  <div className="text-[11px] font-semibold text-emerald-400">
                    Otomatik: yatırım onaylanınca verilir
                    {bonusWindowText(bonus) ? ` • ${bonusWindowText(bonus)}` : ''}
                    {Number(bonus.auto_daily_limit) > 0 ? ` • günde ${Number(bonus.auto_daily_limit)} hak` : ''}
                  </div>
                )}
                {eligibilityRuleLines(bonus).map((line) => (
                  <div key={line} className="text-[11px] font-semibold text-sky-300">{line}</div>
                ))}
                {isCashTierBonus(bonus) && (
                  <div className="text-[11px] font-semibold text-green-300">
                    {`${tierRangeText(bonus)} (${bonusTiers(bonus).length} kademe)`}
                    {Number(bonus.wager_multiplier) > 0
                      ? ` • bonus bakiyesine, x${Number(bonus.wager_multiplier)} çevrim`
                      : ' • ana bakiyeye, çevrimsiz'}
                  </div>
                )}
                {bonus.freespin_enabled && (
                  <div className="text-[11px] font-semibold text-violet-300">
                    {bonusTiers(bonus).length
                      ? `${freespinRewardText(bonus)} (${bonusTiers(bonus).length} kademe)`
                      : Number(bonus.freespin_count) > 0
                        ? `${Number(bonus.freespin_count)} freespin`
                        : 'Freespin (varsayılan adet)'}
                    {bonus.cash_reward_enabled === false ? ' • para ödülü yok' : ' • para ödülüyle birlikte'}
                  </div>
                )}
                <div className="text-[11px] text-slate-400">Kart notu: <span className="font-semibold text-slate-300">{bonusHighlight(bonus) || 'kapalı'}</span></div>
                <div className="flex gap-2">
                  <button onClick={() => openEdit(bonus)} className="admin-btn-primary flex-1 py-2 text-xs"><Edit3 size={12} className="inline mr-1" /> Düzenle</button>
                  <button onClick={() => toggle(bonus)} className="icon-btn view" title={bonus.active ? 'Pasife al' : 'Yayınla'}>{bonus.active ? <EyeOff size={13} /> : <Eye size={13} />}</button>
                  <button onClick={() => remove(bonus.id)} className="icon-btn ban" title="Sil"><Trash2 size={13} /></button>
                </div>
              </div>
            </article>
          ))}
          {!items.length && <div className="admin-card col-span-full p-12 text-center text-slate-500">Henüz bonus kampanyası yok.</div>}
        </div>
      )}

      {modal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" onClick={() => !busy && setModal(null)}>
          <div className="absolute inset-0 bg-black/80 backdrop-blur-md" />
          <div className="relative admin-card admin-modal w-full max-w-3xl" onClick={(event) => event.stopPropagation()}>
            <div className="admin-modal-head">
              <div className="admin-modal-icon"><Gift size={19} /></div>
              <div className="min-w-0">
                <h2 className="font-black text-[15px] leading-tight">
                  {modal.mode === 'new' ? 'Yeni Bonus Kampanyası' : 'Bonus Kampanyasını Düzenle'}
                </h2>
                <p className="text-[11px] text-slate-500 mt-0.5">
                  Ödül, uygunluk ve talep kurallarını tanımlayın
                </p>
              </div>
              <button type="button" onClick={() => setModal(null)} className="icon-btn view ml-auto"><X size={14} /></button>
            </div>

            <div className="admin-modal-body">
              <section className="admin-fieldset">
                <h3 className="admin-fieldset-title"><Sparkles size={12} /> Kampanya</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="sm:col-span-2">
                    <Field label="Bonus Başlığı" hint="Kartın ve detay penceresinin başlığı.">
                      <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="admin-input" placeholder="Örn. İlk Yatırıma %100 Bonus" />
                    </Field>
                  </div>
                  <div className="sm:col-span-2">
                    <DescriptionField
                      value={form.description || ''}
                      onChange={(value) => setForm({ ...form, description: value })}
                      cardHtml={!!form.card_html_enabled}
                      onCardHtmlChange={(value) => setForm({ ...form, card_html_enabled: value })}
                    />
                  </div>
                  <Field label="Rozet" hint="Kart görselinin köşesinde çizilir.">
                    <input value={form.badge} onChange={(e) => setForm({ ...form, badge: e.target.value })} className="admin-input" placeholder="Örn. SPOR" />
                  </Field>
                  <Field label="Sıra" hint="Küçük değer üstte görünür.">
                    <input type="number" value={form.order} onChange={(e) => setForm({ ...form, order: Number(e.target.value) })} className="admin-input" />
                  </Field>
                  <div className="sm:col-span-2">
                    <span className="text-xs text-slate-400 block mb-1.5">Kampanya Görseli</span>
                    <div className="grid gap-3 sm:grid-cols-[170px_1fr]">
                      <div className="relative aspect-[16/9] overflow-hidden rounded-xl border border-white/10 bg-slate-900">
                        {form.image
                          ? <img src={resolveApiAssetUrl(form.image)} alt="Bonus önizlemesi" className="absolute inset-0 h-full w-full object-cover" />
                          : <div className="absolute inset-0 grid place-items-center text-slate-600"><ImagePlus size={28} /></div>}
                      </div>
                      <div className="space-y-2">
                        <label className={`admin-btn-primary flex min-h-10 cursor-pointer items-center justify-center gap-2 ${uploadingImage ? 'pointer-events-none opacity-60' : ''}`}>
                          {uploadingImage ? <Loader2 size={15} className="animate-spin" /> : <ImagePlus size={15} />} Görsel Seç
                          <input type="file" accept="image/jpeg,image/png,image/gif,image/webp,image/svg+xml,.svg" className="sr-only" disabled={uploadingImage} onChange={(e) => { uploadImage(e.target.files?.[0]); e.target.value = ''; }} />
                        </label>
                        <input value={form.image} onChange={(e) => setForm({ ...form, image: e.target.value })} className="admin-input" placeholder="veya görsel URL'si" />
                        <p className="text-[11px] leading-relaxed text-slate-500">
                          Önerilen ölçü 16:9. En fazla {UPLOAD_MAX_LABEL}.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </section>

              {/* Ödülün iki ayağı bağımsızdır: kampanya para, freespin ya da
                  ikisini birden verebilir. Para kapatıldığında yatırım ve
                  uygunluk kuralları yine işler — "500 ₺ yatırana 50 freespin"
                  kurulabilsin diye. */}
              <section className="admin-fieldset">
                <h3 className="admin-fieldset-title"><Coins size={12} /> Ödül</h3>
                <div className="grid gap-2.5">
                  <SwitchGroup
                    icon={Sparkles}
                    label="Deneme bonusu (yatırımsız)"
                    description="Yatırım geçmişi olmayan kullanıcıya para, freespin veya ikisi birlikte verilebilir."
                    checked={!!form.is_trial_bonus}
                    onChange={(isTrial) => setForm({ ...form, is_trial_bonus: isTrial })}
                  />

                  <SwitchGroup
                    icon={Wallet}
                    label="Para ödülü ver"
                    description={form.cash_reward_enabled !== false
                      ? 'Kampanya bakiyeye para yazar.'
                      : 'Kapalı: kampanya bakiyeye hiçbir şey yazmaz, yalnızca freespin verir'
                        + (form.is_trial_bonus
                          ? '; deneme bonusu tutarı gerekmez.'
                          : ' (yatırım şartı yine geçerlidir).')}
                    checked={form.cash_reward_enabled !== false}
                    // Otomatik kampanya tek ödül verir: para açılınca freespin kapanır.
                    onChange={(value) => setForm({
                      ...form,
                      cash_reward_enabled: value,
                      ...(form.auto_award && value ? { freespin_enabled: false } : {}),
                    })}
                  >
                    {form.is_trial_bonus ? (
                      <Field label="Deneme Bonus Tutarı" hint="Sıfırdan büyük olmalıdır.">
                        <Affix text="₺">
                          <input type="number" min="0.01" step="0.01" value={form.trial_amount} onChange={(e) => setForm({ ...form, trial_amount: Number(e.target.value) })} className="admin-input pr-10" />
                        </Affix>
                      </Field>
                    ) : (
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <Field label="Bonus Yüzdesi" hint="Yatırımın yüzde kaçı bonus olarak yazılır.">
                          <Affix text="%">
                            <input type="number" min="0" value={form.bonus_percent} onChange={(e) => setForm({ ...form, bonus_percent: Number(e.target.value) })} className="admin-input pr-10" />
                          </Affix>
                        </Field>
                        <Field label="Max. Bonus" hint="Hesaplanan tutarın üst sınırı.">
                          <Affix text="₺">
                            <input type="number" min="0" value={form.max_bonus} onChange={(e) => setForm({ ...form, max_bonus: Number(e.target.value) })} className="admin-input pr-10" />
                          </Affix>
                        </Field>
                      </div>
                    )}
                  </SwitchGroup>

                  <SwitchGroup
                    icon={Dices}
                    label="Freespin ver"
                    description={'Hak, oyunun sağlayıcısında tanımlanır (bugün Gold Palace). Boş bıraktığınız'
                      + ' alanlar Freespin Kontrol sayfasındaki varsayılana düşer.'}
                    checked={!!form.freespin_enabled}
                    onChange={(value) => setForm({
                      ...form,
                      freespin_enabled: value,
                      ...(form.auto_award && value ? { cash_reward_enabled: false } : {}),
                    })}
                  >
                    <Field
                      label="Oyun"
                      hint={freespinGames.length
                        ? 'Hak bu oyunun sağlayıcısında açılır; oyuncu spinleri o oyunda kullanır.'
                        : undefined}
                    >
                      <select
                        value={form.freespin_game_id || ''}
                        onChange={(e) => setForm({ ...form, freespin_game_id: e.target.value })}
                        className="admin-input"
                      >
                        <option value="">
                          {freespinDefaults?.defaults?.game
                            ? `Varsayılan (${freespinDefaults.defaults.game.name})`
                            : 'Varsayılan (Freespin Kontrol sayfasında tanımlı değil)'}
                        </option>
                        {freespinGames.map((g) => (
                          <option key={g.id} value={g.id}>
                            {g.name} — {g.provider} ({g.api_provider_name})
                          </option>
                        ))}
                      </select>
                    </Field>
                    {!freespinGames.length && (
                      <p className="text-[12px] leading-relaxed rounded-xl border border-amber-500/30 bg-amber-500/10 p-3 text-amber-100">
                        Freespin verilebilecek oyun bulunamadı. Sağlayıcı kapalı olabilir ya da
                        oyunlar henüz senkronize edilmemiş olabilir.
                      </p>
                    )}
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                      <Field label="Spin Adedi" hint={`0 = ${freespinDefaults?.defaults?.count || 0}`}>
                        <Affix text="adet">
                          <input
                            type="number"
                            min="0"
                            className="admin-input pr-10"
                            value={form.freespin_count}
                            onChange={(e) => setForm({ ...form, freespin_count: Number(e.target.value) })}
                          />
                        </Affix>
                      </Field>
                      <Field label="Spin Bahsi" hint={`0 = ${freespinDefaults?.defaults?.bet || 0}`}>
                        <Affix text="₺">
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            className="admin-input pr-10"
                            value={form.freespin_bet}
                            onChange={(e) => setForm({ ...form, freespin_bet: Number(e.target.value) })}
                          />
                        </Affix>
                      </Field>
                      <Field label="Geçerlilik" hint={`0 = ${freespinDefaults?.defaults?.valid_days || 7}`}>
                        <Affix text="gün">
                          <input
                            type="number"
                            min="0"
                            className="admin-input pr-10"
                            value={form.freespin_valid_days}
                            onChange={(e) => setForm({ ...form, freespin_valid_days: Number(e.target.value) })}
                          />
                        </Affix>
                      </Field>
                    </div>
                    <p className="text-[11px] leading-relaxed text-slate-500">
                      Sağlayıcıya o an ulaşılamazsa hak iptal edilmez; Freespin Kontrol
                      sayfasında kuyrukta bekler ve oradan tanımlanır.
                    </p>
                  </SwitchGroup>

                  {/* Kademe tablosu: yatırım tutarına göre değişen ödül. Otomatik
                      kampanyanın bölümünde değil ödülün yanında durur — tablo iki
                      talep yolunda da işler ve Spin Adedi / Bonus Yüzdesi'ni ezer. */}
                  {!form.is_trial_bonus && (rewardsPicked === 1 ? (
                    <BonusTierTable form={form} setForm={setForm} />
                  ) : (form.freespin_tiers || []).length > 0 && (
                    <p className="text-[12px] leading-relaxed rounded-xl border border-amber-500/30 bg-amber-500/10 p-3 text-amber-100">
                      Yatırım kademeleri yalnızca tek ödüllü kampanyada kullanılır:
                      tablodaki sayının spin adedi mi bonus tutarı mı olduğu belirsiz
                      kalırdı. Para ödülü ile freespin&apos;den birini kapatın; tablo o
                      zaman yeniden çizilir.
                    </p>
                  ))}
                </div>
              </section>

              <section className="admin-fieldset">
                <h3 className="admin-fieldset-title"><SlidersHorizontal size={12} /> Uygunluk Kuralları</h3>
                <div className={`grid grid-cols-1 gap-4 ${form.is_trial_bonus ? 'sm:grid-cols-2' : 'sm:grid-cols-3'}`}>
                  {/* Deneme bonusu yatırım aramaz; alanı çizmek yanıltıcı olurdu. */}
                  {!form.is_trial_bonus && (
                    <Field label="Min. Yatırım" hint="Bonusa esas yatırımın alt sınırı.">
                      <Affix text="₺">
                        <input type="number" min="0" value={form.min_deposit} onChange={(e) => setForm({ ...form, min_deposit: Number(e.target.value) })} className="admin-input pr-10" />
                      </Affix>
                    </Field>
                  )}
                  <Field label="Çevrim Şartı" hint="0 = çevrimsiz">
                    <Affix text="×" side="left">
                      <input type="number" min="0" value={form.wager_multiplier} onChange={(e) => setForm({ ...form, wager_multiplier: Number(e.target.value) })} className="admin-input pl-10" />
                    </Affix>
                  </Field>
                  <Field label="Geçerlilik" hint="Bonusun çevrim süresi.">
                    <Affix text="gün">
                      <input type="number" min="1" value={form.valid_days} onChange={(e) => setForm({ ...form, valid_days: Number(e.target.value) })} className="admin-input pr-10" />
                    </Affix>
                  </Field>
                </div>
                {/* Kampanya metinlerinde "slot ve canlı casinoda geçerlidir" gibi
                    birden çok tür geçebiliyor; hiçbiri seçilmezse tüm oyunlar. */}
                <div className="mt-4">
                  <span className="text-xs text-slate-400 block mb-1.5">Geçerli Oyun Türleri</span>
                  <div className="flex flex-wrap gap-2">
                    {BONUS_ELIGIBLE_TYPES.map((item) => {
                      const on = selectedTypes.includes(item.id);
                      return (
                        <button
                          key={item.id}
                          type="button"
                          onClick={() => toggleType(item.id)}
                          aria-pressed={on}
                          className={`admin-chip ${on ? 'active' : ''}`}
                        >
                          {item.name}
                        </button>
                      );
                    })}
                  </div>
                  <p className="mt-1.5 text-[11px] leading-relaxed text-slate-500">
                    {selectedTypes.length
                      ? `Seçili: ${formatBonusEligible(form.eligible)}`
                      : 'Hiçbiri seçili değil — kampanya tüm oyunlarda geçerli sayılır.'}
                  </p>
                </div>
              </section>

              <section className="admin-fieldset">
                <h3 className="admin-fieldset-title"><ShieldCheck size={12} /> Talep Kuralları</h3>
                <div className="grid gap-2.5">
                  {/* Otomatik tanımlama: oyuncu düğmeye basmaz, ödül yatırım
                      onaylanınca arka plan turunda verilir. Tek ödül verir
                      (freespin ya da bakiye); diğer talep yolları kapanır. */}
                  <SwitchGroup
                    icon={Zap}
                    label="Yatırım onaylanınca otomatik tanımla"
                    description={form.auto_award
                      ? 'Oyuncu talep etmez: saat aralığındaki yatırımı onaylanınca ödül en geç'
                        + ' yarım dakika içinde kendiliğinden tanımlanır. Ödül freespin ya da bakiye'
                        + ' olarak seçilir; otomatiği açtığınız andan önceki yatırımlar ödüllenmez.'
                      : 'Açarsanız kampanya kartındaki düğme oyuncuyu yatırıma götürür ve hak'
                        + ' yatırım onaylanınca verilir.'}
                    checked={!!form.auto_award}
                    onChange={(value) => setForm({
                      ...form,
                      auto_award: value,
                      // Tek ödül: yalnızca para açıksa bakiye kalır, aksi hâlde
                      // freespin seçilir (ikisi birden açık olamaz).
                      ...(value ? {
                        freespin_enabled: tierKind(form) !== REWARD_CASH,
                        cash_reward_enabled: tierKind(form) === REWARD_CASH,
                        is_trial_bonus: false,
                        new_members_only: false,
                        claim_via_support: false,
                        claim_redirect_url: '',
                        daily_claim_limit: 0,
                        require_telegram: false,
                      } : {}),
                    })}
                  >
                    <BonusAutoAwardFields form={form} setForm={setForm} />
                    {modal.mode === 'edit' && <BonusAutoAwardLog bonusId={modal.id} />}
                  </SwitchGroup>
                  {/* Otomatik kampanyada talep olmadığı için diğer talep yolları
                      ve talep sınırları anlamsızdır; çizilmezler. */}
                  {!form.auto_award && (
                  <>
                  {/* Talep yolu: kapalı kampanya siteden hiç alınamaz, oyuncu canlı
                      destek temsilcisine yönlendirilir ve bonusu yönetici elle verir. */}
                  <SwitchRow
                    icon={Headset}
                    label="Canlı destekten talep edilir (manuel)"
                    description={form.claim_via_support
                      ? 'Kampanya siteden otomatik alınmaz: oyuncunun butonu canlı destek penceresini'
                        + ' açar, bonusu Kullanıcılar → Üye Profili → Bonus Kontrol üzerinden siz'
                        + ' tanımlarsınız. Talep ucu bu kampanyaya kapalıdır.'
                      : 'Kapalıyken oyuncu bonusu siteden kendisi alır (otomatik).'}
                    checked={!!form.claim_via_support}
                    onChange={(value) => setForm({
                      ...form,
                      claim_via_support: value,
                      // Manuel kampanyada talep kaydı oluşmaz; günlük tavan anlamsızdır.
                      daily_claim_limit: value ? 0 : form.daily_claim_limit,
                      // İki talep yolu birbirini dışlar: yönlendirmeli kampanyada
                      // düğme canlı desteği değil sayfayı açar.
                      claim_redirect_url: value ? '' : form.claim_redirect_url,
                    })}
                  />
                  {/* Ödülü kendi sayfasından alınan kampanyalar (şans çarkı, çapa):
                      kart düğmesi bonusu talep etmez, oyuncuyu oraya götürür. */}
                  <SwitchGroup
                    icon={ExternalLink}
                    label="Kampanya sayfasına yönlendir"
                    description={form.claim_redirect_url
                      ? 'Kart düğmesi bonusu talep etmez, oyuncuyu bu sayfaya götürür;'
                        + ' ödül orada verilir. Talep ucu bu kampanyaya kapalıdır.'
                      : 'Ödülü şans çarkı ya da çapa gibi kendi sayfasından alınan'
                        + ' kampanyalarda kullanın.'}
                    checked={!!form.claim_redirect_url}
                    onChange={(value) => setForm({
                      ...form,
                      claim_redirect_url: value ? '/cark' : '',
                      claim_via_support: value ? false : form.claim_via_support,
                      daily_claim_limit: value ? 0 : form.daily_claim_limit,
                    })}
                  >
                    <Field
                      label="Sayfa adresi"
                      hint="Site içi bir yol olmalıdır: /cark (şans çarkı), /capa (çapa etkinliği)."
                    >
                      <input
                        value={form.claim_redirect_url}
                        onChange={(e) => setForm({ ...form, claim_redirect_url: e.target.value })}
                        placeholder="/cark"
                        className="admin-input"
                      />
                    </Field>
                    <div className="flex flex-wrap gap-2 mt-2">
                      {['/cark', '/capa'].map((path) => (
                        <button
                          key={path}
                          type="button"
                          onClick={() => setForm({ ...form, claim_redirect_url: path })}
                          className={`admin-chip ${form.claim_redirect_url === path ? 'active' : ''}`}
                        >
                          {path === '/cark' ? 'Şans Çarkı' : 'Çapa Etkinliği'}
                        </button>
                      ))}
                    </div>
                  </SwitchGroup>
                  {/* Yatırım bonusu, yatırım oynanmadan alınmalıdır: kapalıyken
                      oyuncu parayı bitirdikten sonra da bonusu talep edebilir. */}
                  {!form.is_trial_bonus && !form.require_lost_deposit && (
                    <SwitchRow
                      icon={BadgeCheck}
                      label="Yatırım oynanmadan talep edilmeli"
                      description={form.require_unspent_deposit !== false
                        ? 'Talep anında ana bakiye, bonusa esas yatırımın altına düşmüşse bonus verilmez'
                          + ' — oyuncu parasını oynadıktan ya da çekim talebi açtıktan sonra bu bonusu'
                          + ' alamaz.'
                        : 'Kapalı: oyuncu yatırdığı parayı harcadıktan sonra da bonusu talep edebilir.'
                          + ' Bonus, yatırımı oynatmak yerine kaybedilen paranın telafisine döner.'}
                      checked={form.require_unspent_deposit !== false}
                      onChange={(value) => setForm({ ...form, require_unspent_deposit: value })}
                    />
                  )}
                  <SwitchRow
                    icon={UserPlus}
                    label="Yalnızca yeni üyeler"
                    // "İlk onaylı yatırıma uygulanır" cümlesi aşamalı
                    // hoşgeldin paketi kuran yöneticiyi yanıltıyordu: anahtar
                    // açıkken kampanya üyenin **ikinci** yatırımıyla birlikte
                    // bonus sayfasından tümüyle kalkar ve alternatif alan da
                    // gizlendiği için görünmez (canlıda bildirildi, 01.10.2026).
                    description={form.is_trial_bonus
                      ? 'Üyelik tarihinden itibaren geçerlilik süresi içinde, yatırım yapmadan alınabilir.'
                      : 'Yalnızca ilk onaylı yatırıma uygulanır ve tek seferliktir:'
                        + ' üye ikinci yatırımını yaptığında kampanya oyuncunun bonus'
                        + ' sayfasından kalkar. Aşamalı hoşgeldin paketi (ilk 3 yatırım)'
                        + ' için bunu kapalı bırakın ve "Yalnızca ilk yatırımlar"'
                        + ' alanına aşama sayısını yazın.'}
                    checked={!!form.new_members_only}
                    onChange={(value) => setForm({
                      ...form,
                      new_members_only: value,
                      daily_claim_limit: value ? 0 : form.daily_claim_limit,
                      // Yeni üye kampanyası zaten ilk yatırıma özeldir.
                      ...(value ? { first_deposits_count: 0, daily_first_deposit: false } : {}),
                    })}
                  />
                  {!form.new_members_only && !form.claim_via_support
                    && !form.claim_redirect_url && !(Number(form.first_deposits_count) > 0)
                    && !form.daily_first_deposit && (
                    <Field
                      label="24 Saatte Talep Hakkı"
                      hint={Number(form.daily_claim_limit) > 0
                        ? `Üye bu kampanyadan 24 saat içinde en fazla ${Number(form.daily_claim_limit)} kez`
                          + ' yararlanır. Her talep ayrı bir onaylı yatırım ister ve önceki bonusun'
                          + ' çevrimi tamamlanmadan yenisi verilmez.'
                        : '0 = kampanya ömür boyu yalnızca bir kez alınır.'}
                    >
                      <Affix text="kez">
                        <input type="number" min="0" max="50" value={form.daily_claim_limit} onChange={(e) => setForm({ ...form, daily_claim_limit: Number(e.target.value) })} className="admin-input pr-10" />
                      </Affix>
                    </Field>
                  )}
                  </>
                  )}
                </div>
              </section>

              <BonusEligibilityFields form={form} setForm={setForm} />

              <section className="admin-fieldset">
                <h3 className="admin-fieldset-title"><Megaphone size={12} /> Kart Notu ve Durum</h3>
                <div className="grid gap-2.5">
                  {/* Kartta çizilen yeşil vurgu satırı. Metin sabit değildir:
                      yönetici kapatabilir ya da kendi cümlesini yazabilir. */}
                  <SwitchGroup
                    icon={Sparkles}
                    label="Kart vurgu notu"
                    description="Bonus kartında ve detay penceresinde yeşil satır olarak çizilir."
                    checked={!!form.highlight_note_enabled}
                    onChange={(value) => setForm({ ...form, highlight_note_enabled: value })}
                  >
                    <Field
                      label="Not metni"
                      hint={form.highlight_note.trim()
                        ? 'Kartta bu metin görünür.'
                        : (autoBonusHighlight(form)
                          ? `Boş bırakıldı — kartta "${autoBonusHighlight(form)}" yazar.`
                          : 'Boş bırakıldı — kartta hiçbir not görünmez.')}
                    >
                      <input
                        value={form.highlight_note}
                        maxLength={160}
                        onChange={(e) => setForm({ ...form, highlight_note: e.target.value })}
                        className="admin-input"
                        placeholder={autoBonusHighlight(form) || 'Örn. Yeni üyelere özel, yatırım gerektirmez'}
                      />
                    </Field>
                  </SwitchGroup>
                  <SwitchRow
                    icon={Power}
                    label="Yayında"
                    description="Kapalıyken kampanya oyuncunun Promosyonlar sayfasında görünmez."
                    checked={!!form.active}
                    onChange={(value) => setForm({ ...form, active: value })}
                  />
                </div>
              </section>
            </div>

            <div className="admin-modal-foot">
              <span className="admin-modal-summary">
                <Gift size={12} />
                <b className="truncate">{form.title.trim() || 'KAMPANYA'}</b>
                <span className="opacity-50">→</span>
                {summaryReward}
              </span>
              <div className="ml-auto flex items-center gap-2">
                <button type="button" onClick={() => setModal(null)} disabled={busy} className="admin-btn-secondary">Vazgeç</button>
                <button type="button" disabled={busy || uploadingImage || !form.title.trim()} onClick={save} className="admin-btn-primary">
                  {busy && <Loader2 size={14} className="animate-spin" />} Kaydet
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// backend/models.py BONUS_DESCRIPTION_MAX_LENGTH ile aynı kalmalıdır.
const DESCRIPTION_MAX_LENGTH = 200000;

// Açıklama alanı düz metin **ya da** ham HTML alır: yönetici başka bir sitenin
// kural şablonunu (kendi <style>'ı, akordeonu) olduğu gibi yapıştırabilir.
// HTML algılandığında önizleme oyuncunun göreceği çerçevenin aynısını çizer
// (components/RichHtmlFrame) — panelde düzgün görünen şablon sitede de aynıdır.
const DescriptionField = ({ value, onChange, cardHtml, onCardHtmlChange }) => {
  const [preview, setPreview] = useState(false);
  const isHtml = looksLikeHtml(value);
  const length = (value || '').length;
  return (
    <div>
      <div className="mb-1.5 flex items-center justify-between gap-2">
        <span className="text-xs text-slate-400">
          Açıklama
          {isHtml && (
            <span className="ml-2 rounded bg-amber-500/15 px-1.5 py-0.5 text-[10px] font-bold text-amber-300">
              HTML
            </span>
          )}
        </span>
        {isHtml && (
          <button
            type="button"
            onClick={() => setPreview((open) => !open)}
            className="inline-flex items-center gap-1 text-[11px] font-semibold text-amber-300 hover:text-amber-200"
          >
            {preview ? <EyeOff size={12} /> : <Eye size={12} />}
            {preview ? 'Önizlemeyi kapat' : 'Önizle'}
          </button>
        )}
      </div>
      <textarea
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className={`admin-input ${isHtml ? 'min-h-32 font-mono text-xs' : 'min-h-20'}`}
        spellCheck={!isHtml}
        placeholder="Kampanyanın koşullarını oyuncunun anlayacağı dille yazın. HTML de yapıştırabilirsiniz."
      />
      <span className="mt-1.5 block text-[11px] leading-relaxed text-slate-500">
        {isHtml
          ? 'HTML algılandı: kendi CSS\'i ve betiğiyle yapıştırılan şablon, bonus'
            + ' detay penceresinde ayrı bir çerçevede çizilir ve sitenin görünümünü'
            + ' bozmaz. Sitenin renklerini var(--pusu-gold) gibi değişkenlerle'
            + ' kullanabilirsiniz. Kartta ne görüneceğini aşağıdaki anahtar seçer.'
          : 'Düz metin yazabilir ya da hazır bir HTML şablonu yapıştırabilirsiniz.'}
        {length > 0 && ` ${length.toLocaleString('tr-TR')} / ${DESCRIPTION_MAX_LENGTH.toLocaleString('tr-TR')} karakter.`}
      </span>
      {isHtml && onCardHtmlChange && (
        <div className="mt-3">
          <SwitchRow
            icon={LayoutTemplate}
            label="Kartta da tasarımıyla göster"
            description={cardHtml
              ? 'Şablonun kendisi (renkleri, başlığı, listesi) kartta ve ana sayfadaki'
                + ' Bonuslar şeridinde küçültülerek çizilir. Görsel yüklemediyseniz'
                + ' görselin yerinde durur; tamamı Detay penceresinde açılır.'
              : 'Kapalıyken kartta ve şeritte şablonun düz metin özeti görünür; tasarım'
                + ' yalnızca Detay penceresinde çizilir.'}
            checked={!!cardHtml}
            onChange={onCardHtmlChange}
          />
        </div>
      )}
      {preview && isHtml && (
        <div className="mt-3 rounded-lg border border-white/10 bg-black/30 p-3">
          <RichHtmlFrame html={value} title="Açıklama önizlemesi" />
        </div>
      )}
    </div>
  );
};

// Kampanyanın uygunluk kuralları. Karar sunucudadır (services/bonus_eligibility.py)
// ve elle talep ile otomatik tanımlamada aynı kurallar işler; burada yalnızca
// yazılır. Yatırıma bağlı kurallar deneme bonusunda çizilmez (yatırım yoktur).
const BonusEligibilityFields = ({ form, setForm }) => {
  const trial = !!form.is_trial_bonus;
  const firstCount = Number(form.first_deposits_count) || 0;
  const hours = Number(form.withdraw_block_hours) || 0;
  const days = parseWeekdays(form.active_weekdays);
  const toggleDay = (id) => setForm({
    ...form,
    active_weekdays: serializeWeekdays(
      days.includes(id) ? days.filter((day) => day !== id) : [...days, id],
    ),
  });
  return (
    <section className="admin-fieldset">
      <h3 className="admin-fieldset-title"><Lock size={12} /> Bonus Kısıtları</h3>
      <p className="text-[11px] leading-relaxed text-slate-500 mb-2.5">
        Kampanya metnindeki şartlar burada işaretlenir ve sistem tarafından uygulanır — hem
        oyuncunun Bonus Al talebinde hem otomatik tanımlamada. Şartı sağlamayan oyuncu bonusu
        alamaz ve nedenini görür.
      </p>
      <div className="grid gap-2.5">
        {!trial && (
          <>
            <SwitchRow
              icon={Lock}
              label="Yatırıma yalnızca bir bonus"
              description={form.exclusive_deposit
                ? 'Bu bonusun verildiği yatırıma başka bir kampanya bonusu verilmez; yatırıma'
                  + ' daha önce bonus verilmişse bu bonus da verilmez.'
                : 'Kapalı: bu kampanya, başka bir kampanyanın (kilitsiz) bonusunu almış'
                  + ' yatırıma da verilebilir.'}
              checked={!!form.exclusive_deposit}
              onChange={(value) => setForm({ ...form, exclusive_deposit: value })}
            />
            <SwitchRow
              icon={Ban}
              label="Bonuslu yatırım kayıp bonusuna girmez"
              description="Bu bonusun verildiği yatırım kayıp bonusu hesabından düşülür: o yatırımla
                oynanıp kaybedilen tutar kayıp bonusu doğurmaz."
              checked={!!form.blocks_loss_bonus}
              onChange={(value) => setForm({ ...form, blocks_loss_bonus: value })}
            />
            {/* İade/kayıp kampanyası. "Yatırım oynanmadan talep edilmeli"
                kuralının aynasıdır: ikisi birlikte açılamaz, bu yüzden bu
                anahtar açılırken diğeri kapatılır (sunucu da reddeder). */}
            <SwitchGroup
              icon={TrendingDown}
              label="Yatırım kaybedildikten sonra alınır (iade bonusu)"
              description={form.require_lost_deposit
                ? 'Talep anında ana bakiye, bonusa esas yatırımın aşağıdaki yüzdesinin üstündeyse'
                  + ' bonus verilmez. "Günün ilk yatırımına %100 iade" gibi kampanyalar için:'
                  + ' bonus yatırımdan hemen sonra değil, para kaybedilince alınır.'
                : 'Kapalı: kampanya sıradan bir yatırım bonusudur, kayıp aranmaz.'}
              checked={!!form.require_lost_deposit}
              onChange={(value) => setForm({
                ...form,
                require_lost_deposit: value,
                // İki kural birbirinin tersidir; açıldığında diğeri kapanır.
                require_unspent_deposit: value ? false : form.require_unspent_deposit,
                // Otomatik ödül yatırım onaylanınca verilir: kayıp aranamaz.
                auto_award: value ? false : form.auto_award,
              })}
            >
              <Field
                label="Kalan bakiye tavanı"
                hint={lostDepositPercent(form) > 0
                  ? `Üye, ana bakiyesi bonusa esas yatırımın %${lostDepositPercent(form)}'ine`
                    + ' düştüğünde bonusu talep edebilir (1.000 ₺ yatırımda'
                    + ` ${(1000 * lostDepositPercent(form)) / 100} ₺ ve altı). Ölçü ana bakiyedir;`
                    + ' bonus cüzdanı ve bekleyen çekim sayılmaz.'
                  : '0 = bakiye tümüyle tükenmiş olmalıdır. Küçük bir bakiyeyle kalan üye'
                    + ' bonusu alamaz; %5-10 arası bir pay şikâyeti azaltır.'}
              >
                <Affix text="%" side="left">
                  <input
                    type="number"
                    min="0"
                    max="100"
                    step="0.5"
                    value={form.lost_deposit_max_percent}
                    onChange={(e) => setForm({
                      ...form, lost_deposit_max_percent: Number(e.target.value),
                    })}
                    className="admin-input admin-input-w-sm pl-10"
                  />
                </Affix>
              </Field>
            </SwitchGroup>
          </>
        )}
        {/* Çekim kuralı: pencere 0 saatse kapalıdır. Varsayılan ölçü net
            giriştir — 2.500 ₺ çekip 1.000 ₺ yatıran alamaz, 5.000 ₺ yatırıp
            500 ₺ çeken alır. Karar sunucudadır (bonus_eligibility). */}
        <SwitchGroup
          icon={ArrowDownToLine}
          label="Çekim kısıtı"
          description={hours > 0
            ? `Yatırımdan önceki ${hours} saat ve o andan bu yana yapılan çekimler (bekleyen`
              + ' dahil) ölçülür. Yöneticinin bakiye düşümü ve reddedilen çekim sayılmaz.'
            : 'Kapalı: çekim geçmişine bakılmaz.'}
          checked={hours > 0}
          onChange={(value) => setForm({
            ...form,
            withdraw_block_hours: value ? WITHDRAW_BLOCK_DEFAULT_HOURS : 0,
          })}
        >
          <Field label="Pencere">
            <Affix text="saat">
              <input type="number" min="1" max="720" value={form.withdraw_block_hours} onChange={(e) => setForm({ ...form, withdraw_block_hours: Number(e.target.value) })} className="admin-input admin-input-w-sm pr-10" />
            </Affix>
          </Field>
          <Field
            label="Ne zaman alamaz"
            hint={withdrawBlockMode(form) === 'any'
              ? 'Pencerede tek bir çekimi olan üye bile bonusu alamaz.'
              : 'Çekimi yatırımını aşan üye alamaz (2.500 ₺ çekip 1.000 ₺ yatıran). Aşağıdaki'
                + ' tutar girilirse net giriş (yatırım − çekim) en az o kadar olmalıdır.'}
          >
            <select
              value={withdrawBlockMode(form)}
              onChange={(e) => setForm({ ...form, withdraw_block_mode: e.target.value })}
              className="admin-input"
            >
              <option value="net">Çekimi yatırımını aşarsa</option>
              <option value="any">Herhangi bir çekimi varsa</option>
            </select>
          </Field>
          {withdrawBlockMode(form) === 'net' && (
            <Field
              label="Asgari net giriş"
              hint={(Number(form.withdraw_block_min_net) || 0) > 0
                ? `Pencerede yatırım − çekim en az ${Number(form.withdraw_block_min_net).toLocaleString('tr-TR')} ₺`
                  + ' olmalıdır; altında kalan üye red gerekçesinde tutarları görür.'
                : '0 = yalnızca çekimin yatırımı aşıp aşmadığına bakılır.'}
            >
              <Affix text="₺">
                <input type="number" min="0" step="50" value={form.withdraw_block_min_net} onChange={(e) => setForm({ ...form, withdraw_block_min_net: Number(e.target.value) })} className="admin-input admin-input-w-sm pr-10" />
              </Affix>
            </Field>
          )}
        </SwitchGroup>
        {/* Alan "Yalnızca yeni üyeler" açıkken gizlenir (ikisi birlikte
            kullanılamaz) — ama sessizce gizlemek, aşamalı paket kurmak
            isteyen yöneticiye seçeneğin var olduğunu hiç söylemiyordu. */}
        {!trial && form.new_members_only && (
          <p className="text-[11px] text-slate-500">
            <b>Yalnızca ilk yatırımlar</b> alanı "Yalnızca yeni üyeler" açıkken
            kullanılamaz. Üç aşamalı hoşgeldin paketi için o anahtarı kapatın;
            alan burada görünür ve kampanya ilk 3 yatırımın her birine ayrı
            ayrı alınır.
          </p>
        )}
        {!trial && !form.new_members_only && !form.daily_first_deposit && (
          <Field
            label="Yalnızca ilk yatırımlar"
            hint={firstCount > 0
              ? `Kampanya üyenin yalnızca ilk ${firstCount} onaylı yatırımında geçerlidir ve her`
                + ' birine ayrı ayrı alınır (ör. hoşgeldin paketinin üç aşaması).'
              : '0 = yatırımın sırasına bakılmaz.'}
          >
            <Affix text="yatırım">
              <input
                type="number"
                min="0"
                max="10"
                value={form.first_deposits_count}
                onChange={(e) => setForm({
                  ...form,
                  first_deposits_count: Number(e.target.value),
                  // Hak sayısını yatırım sayısı belirler.
                  daily_claim_limit: Number(e.target.value) > 0 ? 0 : form.daily_claim_limit,
                })}
                className="admin-input admin-input-w-sm pr-10"
              />
            </Affix>
          </Field>
        )}
        {!trial && (
          <SwitchRow
            icon={History}
            label="İlk yatırımdan sonra çekim yapan alamaz"
            description="Üyenin ilk yatırımından sonra bir çekimi varsa kampanya ona kapanır
              (hoşgeldin paketinin sonraki aşamaları gibi)."
            checked={!!form.stop_after_withdraw}
            onChange={(value) => setForm({ ...form, stop_after_withdraw: value })}
          />
        )}
        {!trial && !form.new_members_only && !(firstCount > 0) && (
          <SwitchRow
            icon={Sunrise}
            label="Yalnızca günün ilk yatırımı"
            description="Bonus yalnızca Türkiye gününün ilk onaylı yatırımına verilir ve her gün bir
              kez alınır; günün ikinci yatırımına verilmez."
            checked={!!form.daily_first_deposit}
            onChange={(value) => setForm({
              ...form,
              daily_first_deposit: value,
              daily_claim_limit: value ? 0 : form.daily_claim_limit,
            })}
          />
        )}
        <div>
          <span className="text-xs text-slate-400 flex items-center gap-1.5 mb-1.5">
            <CalendarDays size={12} /> Geçerli günler
          </span>
          <div className="flex flex-wrap gap-2">
            {WEEKDAYS.map((day) => (
              <button
                key={day.id}
                type="button"
                title={day.label}
                onClick={() => toggleDay(day.id)}
                className={`admin-chip ${days.includes(day.id) ? 'active' : ''}`}
              >
                {day.short}
              </button>
            ))}
            <button type="button" className="admin-chip" onClick={() => setForm({ ...form, active_weekdays: '1,2,3,4,5' })}>
              Hafta içi
            </button>
          </div>
          <span className="mt-1.5 block text-[11px] leading-relaxed text-slate-500">
            {days.length
              ? 'Yatırımın yapıldığı gün (Türkiye saati) seçili günlerden biri olmalıdır.'
              : 'Hiçbir gün seçilmedi: kampanya her gün geçerlidir.'}
          </span>
        </div>
        {/* Otomatik kampanyada aynı alan Talep Kuralları'ndaki otomatik bölümünde
            durur; iki yerde çizilmez. */}
        {!trial && !form.auto_award && (
          <div>
            <span className="text-xs text-slate-400 flex items-center gap-1.5 mb-1.5">
              <Clock size={12} /> Geçerli saatler (Türkiye saati)
            </span>
            <div className="flex flex-wrap items-center gap-2">
              <input
                type="time"
                value={form.deposit_window_start || ''}
                onChange={(e) => setForm({ ...form, deposit_window_start: e.target.value })}
                className="admin-input admin-input-w-sm"
                aria-label="Geçerli saat başlangıcı"
              />
              <span className="text-slate-500">–</span>
              <input
                type="time"
                value={form.deposit_window_end || ''}
                onChange={(e) => setForm({ ...form, deposit_window_end: e.target.value })}
                className="admin-input admin-input-w-sm"
                aria-label="Geçerli saat bitişi"
              />
              {(form.deposit_window_start || form.deposit_window_end) && (
                <button
                  type="button"
                  className="admin-chip"
                  onClick={() => setForm({ ...form, deposit_window_start: '', deposit_window_end: '' })}
                >
                  Temizle
                </button>
              )}
            </div>
            <span className="mt-1.5 block text-[11px] leading-relaxed text-slate-500">
              {form.deposit_window_start && form.deposit_window_end
                ? `Yatırımın açıldığı an ${form.deposit_window_start} – ${form.deposit_window_end}`
                  + ' arasında olmalıdır (bitiş dakikası dahil; gece yarısını geçen aralık'
                  + ' yazılabilir, örn. 22:00 – 02:00). Onay sonradan gelse de yatırımın açıldığı'
                  + ' saat ölçülür.'
                : 'İki alan da boş: kampanya günün her saati geçerlidir. "Her akşam 20.00 –'
                  + ' 23.59" gibi bir şart için ikisini de doldurun.'}
            </span>
          </div>
        )}
        {trial && (
          <SwitchRow
            icon={ListOrdered}
            label="Yatırım/çekim geçmişi olan alamaz"
            description="Daha önce yatırım ya da çekim yapmış üye bu deneme bonusunu alamaz."
            checked={!!form.require_no_finance_history}
            onChange={(value) => setForm({ ...form, require_no_finance_history: value })}
          />
        )}
        {/* Talep anında Telegram'a sorulur; otomatik kampanyada talep yoktur. */}
        {!form.auto_award && (
          <SwitchGroup
            icon={Send}
            label="Telegram kanalına katılım zorunlu"
            description={form.require_telegram
              ? 'Oyuncu bonusu almadan önce Telegram hesabını siteye bağlamalı ve aşağıdaki kanala'
                + ' katılmalıdır; kartta kanala giden düğme çıkar. Üyelik talep anında Telegram\'a sorulur.'
              : 'Açarsanız oyuncu bu bonusu ancak yazdığınız Telegram kanalına katıldıktan sonra alabilir.'}
            checked={!!form.require_telegram}
            onChange={(value) => setForm({ ...form, require_telegram: value })}
          >
            <Field
              label="Kanal adresi"
              hint={'Herkese açık kanal ya da grup adresi (ör. https://t.me/KanalAdiniz). Üyelik botumuzun'
                + ' bu kanala YÖNETİCİ olarak eklenmiş olması gerekir; yoksa üyelik doğrulanamaz. Boş'
                + ' bırakırsanız Ayarlar → Telegram Bildirim\'deki topluluk kullanılır.'}
            >
              <input
                value={form.telegram_channel_url}
                onChange={(e) => setForm({ ...form, telegram_channel_url: e.target.value.trim() })}
                placeholder="https://t.me/KanalAdiniz"
                className="admin-input"
              />
            </Field>
          </SwitchGroup>
        )}
        <SwitchRow
          icon={Ticket}
          label="Promosyon kodu kullanan alamaz"
          description="Daha önce herhangi bir promosyon kodu kullanmış üye bu kampanyadan yararlanamaz."
          checked={!!form.block_if_promo_used}
          onChange={(value) => setForm({ ...form, block_if_promo_used: value })}
        />
        <SwitchRow
          icon={Network}
          label="Aynı IP'den tek üyelik"
          description="Aynı ağ bağlantısından (IP) kampanyayı yalnızca bir üye alabilir. Ölçü talep
            anındaki IP ve kampanyayı almış üyelerin son giriş IP'sidir."
          checked={!!form.one_per_ip}
          onChange={(value) => setForm({ ...form, one_per_ip: value })}
        />
      </div>
    </section>
  );
};

const Field = ({ label, hint, children }) => (
  <label className="block">
    <span className="text-xs text-slate-400 block mb-1.5">{label}</span>
    {children}
    {hint && <span className="mt-1.5 block text-[11px] leading-relaxed text-slate-500">{hint}</span>}
  </label>
);

// Girişin içinde duran birim etiketi (₺, ×, gün): alanın ne beklediğini
// etiketi uzatmadan söyler. Dolgu .admin-input.pl-10 / .pr-10 ile verilir —
// Tailwind'in padding utility'leri .admin-input tarafından eziliyor.
const Affix = ({ text, side = 'right', children }) => (
  <span className={`admin-affix block ${side === 'left' ? 'affix-left' : ''}`}>
    {children}
    <span className="admin-affix-text">{text}</span>
  </span>
);

const SwitchRow = ({ icon: Icon, label, description, checked, onChange }) => (
  <label className={`admin-switch ${checked ? 'on' : ''}`}>
    <input type="checkbox" checked={checked} onChange={(event) => onChange(event.target.checked)} />
    <span className="admin-switch-track" aria-hidden="true" />
    <span className="min-w-0">
      <span className="flex items-center gap-1.5 text-sm font-semibold"><Icon size={13} /> {label}</span>
      <span className="block text-[11px] leading-relaxed text-slate-500 mt-0.5">{description}</span>
    </span>
  </label>
);

// Anahtar + açıkken görünen alanları tek kutuda toplar. Anahtarın kendisi bir
// <label>, alanlar ise onun **kardeşi** olmalıdır: iç içe konan bir input'a
// tıklamak dış label'ı da tetikler ve anahtar kendiliğinden kapanırdı.
const SwitchGroup = ({ checked, children, ...rest }) => (
  <div className={`admin-switch-group ${checked ? 'on' : ''}`}>
    <SwitchRow checked={checked} {...rest} />
    {checked && children && <div className="admin-switch-group-body">{children}</div>}
  </div>
);

export default Bonuses;
